<template>
  <div class="dining-board">
    <div class="top-bar">
      <span class="top-title">餐饮收银台</span>
      <div class="top-right">
        <span v-if="storeName" class="top-store" title="点击切换门店" @click="openStoreModal">{{ storeName }}</span>
        <span class="top-user">{{ userName }}</span>
        <a-button size="small" @click="handleLogout">退出登录</a-button>
      </div>
    </div>
    <div class="board-body">
      <!-- 主区：按区域分组桌台格子（idle 绿 / occupied 红 / reserved 蓝） -->
      <div class="board-main">
        <div class="board-stats">
          <span class="stat idle">空闲 {{ counts.idle }}</span>
          <span class="stat occupied">占用 {{ counts.occupied }}</span>
          <span class="stat reserved">预订 {{ counts.reserved }}</span>
          <span class="stat parked">挂单 {{ parked.length }}</span>
          <a-button v-if="!loading" size="small" class="refresh-btn" @click="refreshAll">刷新</a-button>
          <a-icon v-else type="loading" class="refresh-btn" />
        </div>
        <div class="area-scroll">
          <div class="area-block" v-for="area in areas" :key="area.key">
            <div class="area-title">
              {{ area.name }}
              <span class="area-count">{{ area.tables.length }} 桌</span>
            </div>
            <div class="table-grid">
              <div class="table-card" v-for="t in area.tables" :key="t.id"
                   :class="t.status" @click="onTableClick(t)">
                <div class="table-head">
                  <span class="table-name">{{ t.name }}</span>
                  <span class="table-cap">{{ t.capacity || '-' }}人</span>
                </div>
                <template v-if="t.orderId">
                  <div class="table-order-no">{{ t.orderNo }}</div>
                  <div class="table-amount" v-if="amountOf(t.orderId) !== null">¥{{ amountOf(t.orderId) }}</div>
                </template>
                <div class="table-status-text" v-else>{{ statusText(t.status) }}</div>
              </div>
            </div>
          </div>
          <div v-if="!loading && areas.length === 0" class="board-empty">
            暂无桌台，请先在管理端配置桌台
          </div>
        </div>
      </div>
      <!-- 副区：挂单列表 + 外带单 + 订单管理入口 -->
      <div class="board-side">
        <div class="side-actions">
          <a-button type="primary" block icon="shopping" :loading="creating" @click="createTakeout">外带单</a-button>
          <a-button block icon="profile" @click="manageVisible = true">订单管理</a-button>
        </div>
        <div class="side-title">挂单（{{ parked.length }}）</div>
        <div class="parked-scroll">
          <div class="parked-card" v-for="p in parked" :key="p.id" @click="resumeParked(p)">
            <div class="p-head">
              <span class="p-table">{{ p.tableName || '外带' }}</span>
              <span class="p-amount">¥{{ fmt(p.total_amount) }}</span>
            </div>
            <div class="p-no">{{ p.order_no }}</div>
            <div class="p-time">{{ p.update_time }}</div>
          </div>
          <div v-if="parked.length === 0" class="side-empty">暂无挂单</div>
        </div>
      </div>
    </div>
    <OrderManage v-if="manageVisible" :store-id="storeId" @close="manageVisible = false" />
    <!-- 门店切换弹窗：未绑门店账号（如超管）进入时自动弹出，顶栏门店名点击亦可切换 -->
    <a-modal title="选择门店" :visible="storeModalVisible" :footer="null" :maskClosable="false" @cancel="storeModalVisible = false">
      <div class="store-pick" v-for="s in storeOptions" :key="s.id" @click="pickStore(s)">
        <span class="store-pick-name">{{ s.name }}</span>
        <a-tag v-if="s.isMain === '1'" color="blue">总店</a-tag>
        <a-tag :color="s.status === '1' ? 'green' : 'red'">{{ s.status === '1' ? '营业' : '停用' }}</a-tag>
      </div>
      <div v-if="storeOptions.length === 0" class="store-pick-empty">当前租户尚未创建门店，请先在管理后台创建门店</div>
    </a-modal>
  </div>
</template>

<script>
import Vue from 'vue'
import { USER_INFO, ACCESS_TOKEN } from '@/store/mutation-types'
import { storeMy, storeList, tableList, parkedList, orderList, orderCreate, orderResume } from '@/api/plugin/dining'
import OrderManage from './OrderManage'

const POLL_INTERVAL = 15000 // 桌台状态轮询间隔

/** 餐饮收银台桌台总览（/cashier/dining）：开台/进单/挂单取单/外带/订单管理 + 15s 轮询（页面隐藏停止） */
export default {
  name: 'DiningCashierBoard',
  components: { OrderManage },
  data() {
    return {
      userName: '',
      storeName: '',
      storeId: null,
      tables: [],
      parked: [],
      amountMap: {}, // orderId -> total_amount（订单列表/挂单列表联查金额，均为后端口径）
      loading: false,
      creating: false,
      manageVisible: false,
      storeOptions: [], // 门店切换候选（未绑门店账号兜底 + 顶栏手动切换）
      storeModalVisible: false,
      pollTimer: null
    }
  },
  computed: {
    areas() {
      // 桌台列表按 area_id 排序返回，按区域名分组保持顺序
      const list = []
      const index = {}
      this.tables.forEach(t => {
        const name = t.areaName || '未分区'
        if (index[name] === undefined) {
          index[name] = list.length
          list.push({ key: name, name: name, tables: [] })
        }
        list[index[name]].tables.push(t)
      })
      return list
    },
    counts() {
      const c = { idle: 0, occupied: 0, reserved: 0 }
      this.tables.forEach(t => {
        if (c[t.status] !== undefined) c[t.status]++
      })
      return c
    }
  },
  created() {
    this.initUserInfo()
    this.resolveStore()
  },
  mounted() {
    document.addEventListener('visibilitychange', this.onVisibilityChange)
  },
  beforeDestroy() {
    this.stopPolling()
    document.removeEventListener('visibilitychange', this.onVisibilityChange)
  },
  methods: {
    initUserInfo() {
      const info = Vue.ls.get(USER_INFO)
      this.userName = (info && (info.username || info.loginName)) || ''
    },
    // 门店上下文：sessionStorage('diningStore')（Login 登录后写入），缺失时 storeMy 兜底
    resolveStore() {
      let store = null
      try {
        store = JSON.parse(sessionStorage.getItem('diningStore') || 'null')
      } catch (e) {
        store = null
      }
      if (store && store.id) {
        this.applyStore(store)
        return
      }
      storeMy().then(res => {
        if (res.code === 200 && res.data && res.data.id) {
          sessionStorage.setItem('diningStore', JSON.stringify(res.data))
          this.applyStore(res.data)
        } else {
          // 未绑定门店的账号（如超管）：拉门店列表兜底，单店自动进入，多店弹选择
          this.fallbackStoreList()
        }
      }).catch(() => {
        this.fallbackStoreList()
      })
    },
    // 门店列表兜底/切换：单店自动应用并缓存；多店弹选择；无店提示引导后台建店
    // store/list 返回 {rows, total}（分页结构），rows 为门店数组
    fallbackStoreList() {
      storeList({ pageSize: 100 }).then(res => {
        const rows = (res.code === 200 && res.data && res.data.rows) || []
        if (rows.length === 1) {
          this.applyStoreAndCache(rows[0])
        } else if (rows.length > 1) {
          this.storeOptions = rows
          this.storeModalVisible = true
        } else {
          this.$message.error('当前租户尚未创建门店，请先在管理后台创建门店')
        }
      }).catch(() => {
        this.$message.error('获取门店信息失败，请重新登录')
        this.$router.push('/cashier/login')
      })
    },
    applyStoreAndCache(store) {
      sessionStorage.setItem('diningStore', JSON.stringify(store))
      this.applyStore(store)
    },
    // 顶栏门店名点击切换：重新拉候选列表（保证新建门店及时可见）
    openStoreModal() {
      storeList({ pageSize: 100 }).then(res => {
        if (res.code === 200 && res.data && res.data.rows) {
          this.storeOptions = res.data.rows
        }
      })
      this.storeModalVisible = true
    },
    pickStore(s) {
      this.storeModalVisible = false
      if (s && s.id) {
        this.applyStoreAndCache(s)
      }
    },
    applyStore(store) {
      this.storeId = store.id
      this.storeName = store.name || ''
      this.refreshAll()
      this.startPolling()
    },
    fmt(v) {
      return Number(v || 0).toFixed(2)
    },
    statusText(s) {
      return { idle: '空闲', occupied: '占用中', reserved: '已预订' }[s] || s
    },
    amountOf(orderId) {
      const v = this.amountMap[orderId]
      return v === undefined ? null : this.fmt(v)
    },
    // 轮询刷新：桌台列表 + 挂单列表 + 近百笔订单（联查当前订单金额展示）
    refreshAll() {
      if (!this.storeId) return
      this.loading = true
      Promise.all([
        tableList({ storeId: this.storeId }),
        parkedList({ storeId: this.storeId }),
        orderList({ storeId: this.storeId, currentPage: 1, pageSize: 100 })
      ]).then(([tables, parked, orders]) => {
        if (tables.code === 200 && tables.data) {
          this.tables = tables.data || []
        }
        const amountMap = {}
        if (parked.code === 200 && parked.data && parked.data.rows) {
          this.parked = parked.data.rows || []
          this.parked.forEach(p => { amountMap[p.id] = p.total_amount })
        }
        if (orders.code === 200 && orders.data && orders.data.rows) {
          orders.data.rows.forEach(o => { amountMap[o.id] = o.total_amount })
        }
        this.amountMap = amountMap
      }).catch(() => {}).finally(() => {
        this.loading = false
      })
    },
    startPolling() {
      this.stopPolling()
      this.pollTimer = setInterval(this.refreshAll, POLL_INTERVAL)
    },
    stopPolling() {
      if (this.pollTimer) {
        clearInterval(this.pollTimer)
        this.pollTimer = null
      }
    },
    onVisibilityChange() {
      // 页面隐藏停止轮询；恢复可见立即刷新并重启轮询
      if (document.hidden) {
        this.stopPolling()
      } else if (this.storeId) {
        this.refreshAll()
        this.startPolling()
      }
    },
    // 空闲/预订桌点击 = 开台（dine_in）；占用桌点击 = 进入当前订单
    onTableClick(t) {
      if (this.loading) return
      if (t.status === 'idle' || t.status === 'reserved') {
        this.openTable(t)
      } else if (t.status === 'occupied') {
        if (t.orderId) {
          this.gotoOrder(t.id, t.orderId)
        } else {
          this.$message.warning('桌台状态同步中，请刷新后重试')
          this.refreshAll()
        }
      }
    },
    openTable(t) {
      if (this.creating) return
      this.creating = true
      orderCreate({ storeId: this.storeId, orderType: 'dine_in', tableId: t.id }).then(res => {
        if (res.code === 200 && res.data) {
          this.gotoOrder(t.id, res.data.id)
        } else {
          this.$message.error(res.message || '开台失败')
          this.refreshAll()
        }
      }).catch(() => {
        this.$message.error('开台失败，请重试')
      }).finally(() => {
        this.creating = false
      })
    },
    gotoOrder(tableId, orderId) {
      this.$router.push(`/cashier/dining/order/${tableId || 0}?orderId=${orderId}&storeId=${this.storeId}`)
    },
    // 挂单取单：resume 后进入点餐面板
    resumeParked(p) {
      if (this.creating) return
      this.creating = true
      orderResume(p.id).then(res => {
        if (res.code === 200) {
          this.gotoOrder(p.table_id || 0, p.id)
        } else {
          this.$message.error(res.message || '取单失败')
          this.refreshAll()
        }
      }).catch(() => {
        this.$message.error('取单失败，请重试')
      }).finally(() => {
        this.creating = false
      })
    },
    createTakeout() {
      if (this.creating) return
      this.creating = true
      orderCreate({ storeId: this.storeId, orderType: 'takeout' }).then(res => {
        if (res.code === 200 && res.data) {
          this.gotoOrder(0, res.data.id)
        } else {
          this.$message.error(res.message || '外带单创建失败')
        }
      }).catch(() => {
        this.$message.error('外带单创建失败，请重试')
      }).finally(() => {
        this.creating = false
      })
    },
    handleLogout() {
      this.stopPolling()
      this.$store.dispatch('Logout').then(() => {
        Vue.ls.remove(ACCESS_TOKEN)
        sessionStorage.removeItem('diningStore')
        this.$router.push('/cashier/login')
      })
    }
  }
}
</script>

<style scoped>
.dining-board {
  height: 100vh;
  display: flex;
  flex-direction: column;
  background: #f0f2f5;
  overflow: hidden;
}
.top-bar {
  height: 56px;
  flex-shrink: 0;
  background: #001529;
  color: #fff;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0 20px;
}
.top-title {
  font-size: 17px;
  font-weight: 600;
}
.top-right {
  display: flex;
  align-items: center;
}
.top-store {
  color: rgba(255, 255, 255, 0.75);
  font-size: 13px;
  margin-right: 14px;
  cursor: pointer;
}
.top-store:hover {
  color: #fff;
}
.store-pick {
  display: flex;
  align-items: center;
  padding: 10px 12px;
  border: 1px solid #e8e8e8;
  border-radius: 4px;
  margin-bottom: 8px;
  cursor: pointer;
}
.store-pick:hover {
  border-color: #1890ff;
  background: #e6f7ff;
}
.store-pick-name {
  flex: 1;
  font-size: 14px;
}
.store-pick-empty {
  text-align: center;
  color: #999;
  padding: 16px 0;
}
.top-user {
  font-size: 13px;
  margin-right: 14px;
}
.board-body {
  flex: 1;
  display: flex;
  min-height: 0;
}
.board-main {
  flex: 1;
  display: flex;
  flex-direction: column;
  min-width: 0;
  padding: 14px 16px 14px 20px;
}
.board-stats {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 12px;
  flex-shrink: 0;
}
.stat {
  font-size: 13px;
  border-radius: 12px;
  padding: 2px 12px;
}
.stat.idle { background: #f6ffed; color: #52c41a; }
.stat.occupied { background: #fff1f0; color: #f5222d; }
.stat.reserved { background: #e6f7ff; color: #1890ff; }
.stat.parked { background: #f9f0ff; color: #722ed1; }
.refresh-btn {
  margin-left: auto;
  color: #999;
}
.area-scroll {
  flex: 1;
  overflow-y: auto;
  min-height: 0;
}
.area-block {
  margin-bottom: 18px;
}
.area-title {
  font-size: 15px;
  font-weight: 600;
  color: #333;
  margin-bottom: 10px;
}
.area-count {
  font-size: 12px;
  color: #999;
  font-weight: 400;
  margin-left: 8px;
}
.table-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(150px, 1fr));
  gap: 12px;
}
.table-card {
  background: #fff;
  border-radius: 8px;
  border: 1px solid #e8e8e8;
  border-top: 4px solid #d9d9d9;
  padding: 10px 12px;
  cursor: pointer;
  min-height: 86px;
  display: flex;
  flex-direction: column;
  transition: box-shadow 0.2s;
  user-select: none;
}
.table-card:hover {
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.12);
}
.table-card.idle { border-top-color: #52c41a; }
.table-card.occupied { border-top-color: #f5222d; }
.table-card.reserved { border-top-color: #1890ff; }
.table-head {
  display: flex;
  align-items: baseline;
  justify-content: space-between;
}
.table-name {
  font-size: 16px;
  font-weight: 600;
  color: #333;
}
.table-cap {
  font-size: 12px;
  color: #999;
}
.table-order-no {
  font-size: 12px;
  color: #f5222d;
  margin-top: 6px;
  word-break: break-all;
}
.table-amount {
  font-size: 17px;
  font-weight: 600;
  color: #e4393c;
  margin-top: 2px;
}
.table-status-text {
  margin-top: auto;
  font-size: 13px;
  color: #999;
  align-self: flex-start;
}
.board-empty {
  color: #999;
  text-align: center;
  padding: 60px 0;
  font-size: 14px;
}
.board-side {
  width: 300px;
  flex-shrink: 0;
  background: #fff;
  border-left: 1px solid #e8e8e8;
  display: flex;
  flex-direction: column;
  padding: 14px 16px;
}
.side-actions {
  display: flex;
  flex-direction: column;
  gap: 10px;
  margin-bottom: 14px;
  flex-shrink: 0;
}
.side-title {
  font-size: 14px;
  font-weight: 600;
  color: #333;
  margin-bottom: 10px;
  flex-shrink: 0;
}
.parked-scroll {
  flex: 1;
  overflow-y: auto;
  min-height: 0;
}
.parked-card {
  border: 1px solid #e8e8e8;
  border-radius: 6px;
  padding: 10px 12px;
  margin-bottom: 10px;
  cursor: pointer;
}
.parked-card:hover {
  border-color: #722ed1;
  box-shadow: 0 2px 8px rgba(114, 46, 209, 0.15);
}
.p-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
}
.p-table {
  font-size: 14px;
  font-weight: 600;
  color: #333;
}
.p-amount {
  font-size: 15px;
  font-weight: 600;
  color: #e4393c;
}
.p-no {
  font-size: 12px;
  color: #999;
  margin-top: 4px;
}
.p-time {
  font-size: 12px;
  color: #bbb;
  margin-top: 2px;
}
.side-empty {
  color: #ccc;
  text-align: center;
  padding: 30px 0;
  font-size: 13px;
}
</style>
